import type {
  AmmTelemetryView,
  CandleSeries,
  InstrumentTelemetryView,
  Percent,
  Quantity,
  Rate,
  TradeView,
} from '@nutrimatic/sdk'

import type { MarketCandle, RecentTrade } from '@/data/marketApi/types'

export function shortNutrimaticAddr(addr: string | undefined | null, chars = 4): string {
  if (!addr) return '—'
  if (addr.length <= chars * 2 + 2) return addr
  return `${addr.slice(0, chars + 2)}…${addr.slice(-chars)}`
}

export function formatNutrimaticQuantity(q: Quantity | undefined | null): string {
  if (!q) return '—'
  try {
    const raw = typeof q.raw === 'bigint' ? q.raw : BigInt(q.raw)
    const decimals = q.decimals
    if (decimals <= 0) return raw.toString()
    const neg = raw < 0n
    const abs = neg ? -raw : raw
    const base = 10n ** BigInt(decimals)
    const whole = abs / base
    const frac = abs % base
    if (frac === 0n) return `${neg ? '-' : ''}${whole.toString()}`
    const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '').slice(0, 6)
    return `${neg ? '-' : ''}${whole.toString()}.${fracStr}`
  } catch {
    return String(q.raw)
  }
}

export function formatNutrimaticUsd(n: number | undefined | null): string {
  if (n == null || !Number.isFinite(n)) return '—'
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}K`
  return `$${n.toFixed(2)}`
}

export function formatNutrimaticPrice(rate: Rate | null | undefined): string {
  if (!rate) return '—'
  const v = typeof rate.value === 'number' ? rate.value : Number(rate.value)
  if (!Number.isFinite(v)) return String(rate.value)
  if (v >= 1000) return v.toFixed(2)
  if (v >= 1) return v.toFixed(4)
  return v.toPrecision(4)
}

export function formatNutrimaticChange(change: Percent | null | undefined): string {
  if (change == null || !Number.isFinite(change.value)) return '—'
  const pct = change.value * 100
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}

export function formatPoolRowMetrics(pool: AmmTelemetryView | null | undefined): {
  price: string
  change24h: string
  volume24h: string
  tvl: string
  tvlUsd: number
} {
  return {
    price: formatNutrimaticPrice(pool?.spot),
    change24h: formatNutrimaticChange(pool?.change24h),
    volume24h: formatNutrimaticUsd(pool?.volume24hUsd),
    tvl: formatNutrimaticUsd(pool?.tvlUsd),
    tvlUsd: pool?.tvlUsd ?? 0,
  }
}

export function formatInstrumentPrice(view: InstrumentTelemetryView | null | undefined): {
  priceUsd: string
  change24h: string
  volume24h: string
  liquidityUsd: string
} {
  return {
    priceUsd:
      view?.priceUsd != null && Number.isFinite(view.priceUsd)
        ? formatNutrimaticUsd(view.priceUsd)
        : '—',
    change24h: formatNutrimaticChange(view?.change24h),
    volume24h: formatNutrimaticUsd(view?.market?.volume24hUsd),
    liquidityUsd: formatNutrimaticUsd(view?.market?.liquidityUsd),
  }
}

export function candleSeriesToMarketCandles(series: CandleSeries | null | undefined): MarketCandle[] {
  if (!series) return []
  const byTime = new Map<number, MarketCandle>()
  for (const c of series.candles) {
    byTime.set(c.time, {
      time: c.time,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    })
  }
  if (series.partialCandle) {
    const p = series.partialCandle
    byTime.set(p.time, {
      time: p.time,
      open: p.open,
      high: p.high,
      low: p.low,
      close: p.close,
    })
  }
  return [...byTime.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, c]) => c)
}

export function tradeViewsToRecent(events: readonly TradeView[] | undefined): RecentTrade[] {
  if (!events?.length) return []
  return [...events]
    .slice()
    .reverse()
    .map((t) => ({
      id: t.eventId,
      price: String(t.price),
      amount: String(t.baseAmount),
      side: t.side,
      time: new Date(t.time * 1000).toISOString(),
      txHash: t.txHash,
    }))
}
